import React from 'react';
import { Box, Icon, Badge } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { reduxHelper } from '../../../config/reduxHelper';
import { useCart } from '../../../hooks/useCart';
import { useEffect } from 'react';

/**
 * FlexibleIconBlock - A flexible replacement for standalone icons.
 * Supports MDI icons, cart badges, and specialized navigation.
 */
const FlexibleIconBlock = ({ block, isSiteBuilder, onUpdate }) => {
    const { push } = useRouter();
    const dispatch = useDispatch();
    const { cartItems, siteBuilderSelectedChild } = useSelector((state) => state.reducer);

    const isSelected = isSiteBuilder && siteBuilderSelectedChild?.uid === block.uid;
    const isCart = block.props?.isCart || block.url?.path === '/cart';

    const getCartCount = async () => {
        const result = await useCart();
        dispatch({
            type: reduxHelper.UPDATE_CART_ITEMS,
            payload: result,
        });
    };

    useEffect(() => {
        if (!isSiteBuilder && isCart) {
            getCartCount();
        }
    }, [isCart, isSiteBuilder]);

    const handleClick = (e) => {
        if (isSiteBuilder) {
            e.stopPropagation();
            if (siteBuilderSelectedChild?.uid !== block.uid) {
                dispatch({
                    type: reduxHelper.UPDATE_SITE_BUILDER_SELECTED_CHILD,
                    payload: { ...block },
                });
            }
            return;
        }

        if (block.url) {
            const url = block.url;
            if (typeof url === 'string') {
                push(url);
            } else if (url.path) {
                if (url.target) {
                    window.open(url.path, "_blank", "noopener,noreferrer");
                } else {
                    push(url.path);
                }
            }
        }
    };

    const iconName = block.children || block.props?.icon || 'help';

    return (
        <Box
            onClick={handleClick}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: isSiteBuilder || block.url ? 'pointer' : 'default',
                outline: isSelected ? '1px solid #1890ff' : 'none',
                outlineOffset: '2px',
                borderRadius: '4px',
                padding: '4px',
                transition: 'all 0.2s',
                '&:hover': {
                    bgcolor: isSiteBuilder ? 'rgba(24, 144, 255, 0.05)' : 'transparent',
                },
                ...block.props?.sx,
            }}
        >
            <Badge badgeContent={isCart ? cartItems?.length : null} color="primary">
                <Icon
                    className={`mdi mdi-${iconName}`}
                    sx={{
                        fontSize: block.props?.fontSize || '24px',
                        color: block.props?.style?.color || block.props?.color || 'inherit',
                        height: 'auto',
                        width: 'auto',
                        ...block.props?.iconSx
                    }}
                />
            </Badge>
        </Box>
    );
};

export default FlexibleIconBlock;
